import { Button, Form, Space } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { debounce } from "lodash";
import { globalNotificationState } from "../../../../../../_common/global_state";
import { useSaveUserVerificationHistory } from "../../../../function/accountVerification";
import { buildSteps, cleanStep, isDocumentFilled, isStepFilled, extractLocalizedValue} from "../../../../common";
import { VerificationStep, isPendingLikeStatus } from "../../../../common/constant";
import { initializeDocumentForm, renderFormFields } from "../../../../common/form";
import { createLogSession } from "../../../../../../api/restful/logs/logs";

function DocumentForm(props) {
    const {
        currentHistory,
        selectedTier,
        selectedDocument,
        selectedInnerDocument,
        goBack,
        goTo,
        refetchUserVerificationHistories
    } = props

    const { t } = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState)

    const { saveUserVerificationHistory, loading: saveLoading } = useSaveUserVerificationHistory();

    const [form] = Form.useForm();
    const [saveStatus, setSaveStatus] = useState("idle"); // for label
    const [isSaving, setIsSaving] = useState(false); // for button

    const isPendingVerification = isPendingLikeStatus(currentHistory?.status)

    const fields = (selectedInnerDocument || selectedDocument)?.steps?.map((step) => ({
        ...step.sourceDoc,
        isRequired: step.isRequired,
        ...(step.sourceDoc?.type === 'questionnaire' && { formSettingId: step.questionnaireId })
    })) || [];

    const debouncedSave = useRef(
        debounce(async (payload) => {
            try {
                setIsSaving(true)
                const result = await saveUserVerificationHistory(payload);
                setSaveStatus(result?.status === "Success" ? "saved" : "idle");
            } catch {
                setSaveStatus("idle");
            } finally {
                setIsSaving(false)
                setTimeout(() => setSaveStatus("idle"), 2000);
                await refetchUserVerificationHistories();
            }
        }, 1500)
    ).current;

    useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

    const handleAutoSave = useCallback(async () => {
        if (!currentHistory?._id || !selectedDocument?._id) return;

        const formValues = form.getFieldsValue();
        let innerDocuments

        let updatedDoc = {
            _id: selectedDocument._id,
            steps: buildSteps(formValues, selectedDocument.steps),
        };

        if (selectedInnerDocument) {
            innerDocuments = {
                _id: selectedInnerDocument._id,
                steps: buildSteps(formValues, selectedInnerDocument.steps),
            }

            updatedDoc = {
                _id: selectedDocument._id,
                steps: updatedDoc.steps.map((step) => {
                    if (step.sourceId === innerDocuments._id) {
                        return {
                            ...step,
                            value: { steps: innerDocuments.steps }
                        }
                    }

                    const found = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id) || []
                    const foundInnerDoc = found?.steps?.find(doc => doc?.sourceId === step?.sourceId)
                    const isDocEmpty = !(foundInnerDoc?.value?.steps?.some(isStepFilled))

                    return isDocEmpty ? { sourceId: step.sourceId, value: null } : foundInnerDoc ? {
                        ...foundInnerDoc,
                        sourceType: step.sourceType
                    } : step
                })
            }
        }

        const mergedDocuments = [...(currentHistory.documents || [])];
        const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);
        if (!isDocumentFilled(updatedDoc)) {
            // nothing filled — drop the doc so empty records aren't persisted
            if (index >= 0) mergedDocuments.splice(index, 1);
        } else if (index >= 0) {
            mergedDocuments[index] = updatedDoc;
        } else {
            mergedDocuments.push(updatedDoc);
        }

        const payload = {
            _id: currentHistory._id,
            documents: mergedDocuments.map(doc => ({
                ...doc,
                steps: doc.steps?.map(cleanStep)
            }))
        };

        setIsSaving(true);
        setSaveStatus("saving");

        debouncedSave(payload);
    }, [currentHistory._id, currentHistory.documents, selectedDocument._id, selectedDocument.steps, selectedInnerDocument, form, debouncedSave]);

    const handleSaveDocument = async () => {
        const log = createLogSession("User", "Verification", "save");
        debouncedSave.cancel();

        try {
            if (!currentHistory?._id || !selectedDocument?._id) return;

            const formValues = form.getFieldsValue();
            let innerDocuments

            let updatedDoc = {
                _id: selectedDocument._id,
                steps: buildSteps(formValues, selectedDocument.steps),
            };

            if (selectedInnerDocument) {
                innerDocuments = {
                    _id: selectedInnerDocument._id,
                    steps: buildSteps(formValues, selectedInnerDocument.steps),
                }

                updatedDoc = {
                    _id: selectedDocument._id,
                    steps: updatedDoc.steps.map((step) => {
                        if (step.sourceId === innerDocuments._id) {
                            return {
                                ...step,
                                value: { steps: innerDocuments.steps }
                            }
                        }

                        const found = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id) || []
                        const foundInnerDoc = found?.steps?.find(doc => doc?.sourceId === step?.sourceId)

                        const isDocEmpty = !(foundInnerDoc?.value?.steps?.some(isStepFilled))

                        return isDocEmpty ? { sourceId: step.sourceId, value: null } : foundInnerDoc ? {
                            ...foundInnerDoc,
                            sourceType: step.sourceType
                        } : step
                    })
                }
            }

            const mergedDocuments = [...(currentHistory.documents || [])];
            const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);
            if (!isDocumentFilled(updatedDoc)) {
                // nothing filled — drop the doc so empty records aren't persisted
                if (index >= 0) mergedDocuments.splice(index, 1);
            } else if (index >= 0) {
                mergedDocuments[index] = updatedDoc;
            } else {
                mergedDocuments.push(updatedDoc);
            }

            const payload = {
                _id: currentHistory._id,
                documents: mergedDocuments.map(doc => ({
                    ...doc,
                    steps: doc.steps?.map(cleanStep)
                }))
            };

            const result = await saveUserVerificationHistory(payload);
            log.create({ status: result?.status === "Success" ? "info" : "error", description: result?.status === "Success" ? "Document saved" : "Save document failed", logData: result });

            if (result?.status === "Success") {
                await refetchUserVerificationHistories();

                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t("accountVerification.saveSuccessfully"),
                    key: "accountVerificationNotification",
                });

                goTo(selectedInnerDocument ? VerificationStep.DocumentGroup : VerificationStep.Document);
            }

        } catch (error) {
            log.create({ status: "error", description: "Save document exception", logData: error });
            console.error("failed to save document")
        }
    }

    useEffect(() => {
        if (!currentHistory) return;

        const currentDoc = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id)
        if (selectedInnerDocument && currentDoc?.steps?.length > 0) {
            const found = currentDoc?.steps?.find(doc => doc?.sourceId === selectedInnerDocument?._id)
            const currentInnerDoc = { _id: found?.sourceId, steps: found?.value?.steps || [] }

            const initialValues = initializeDocumentForm(currentInnerDoc, selectedInnerDocument)
            form.setFieldsValue(initialValues)

            return
        }

        const initialValues = initializeDocumentForm(currentDoc, selectedDocument)
        form.setFieldsValue(initialValues)
    }, [currentHistory, selectedDocument, form, selectedInnerDocument])

    return (
        <>
            {saveStatus !== "idle" && (
                <div
                    style={{
                        textAlign: "right",
                        color: saveStatus === "saving" ? "#888" : "green",
                        marginBottom: 8,
                        transition: "all 0.3s",
                    }}
                >
                    {saveStatus === "saving"
                        ? t("accountVerification.saving")
                        : t("accountVerification.saved")}
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveDocument}
                onValuesChange={() => {
                    handleAutoSave();
                }}
            >
                {
                    renderFormFields(t, form, fields, currentHistory?.status, handleAutoSave, [], {
                        layout: "layout_1",
                        // A liveness step needs all three ids to create its capture link;
                        // they're already here, so thread them down rather than re-fetching.
                        historyId: currentHistory?._id,
                        documentSettingId: (selectedInnerDocument || selectedDocument)?._id,
                        // Liveness cards are titled from the document, not the field.
                        documentLabel: extractLocalizedValue((selectedInnerDocument || selectedDocument)?.displayName)
                            || (selectedInnerDocument || selectedDocument)?.name,
                        documentDescription: extractLocalizedValue((selectedInnerDocument || selectedDocument)?.description),
                    })
                }

                <Form.Item>
                    <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            onClick={() => {
                                goTo(selectedInnerDocument ? VerificationStep.DocumentGroup : VerificationStep.Document)
                            }}
                            disabled={isSaving}
                        >
                            Back
                        </Button>


                        {
                            !isPendingVerification
                            && (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={saveLoading}
                                    disabled={saveLoading}
                                >
                                    Save
                                </Button>
                            )
                        }

                    </Space>
                </Form.Item>
            </Form>
        </>

    )
}

export default DocumentForm;
